/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import mbpLogger from 'mbp-logger';
import { useMutation } from 'react-query';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import * as auth from '../../../../../state/ducks/Member/ducks/Auth/Plugin/auth/auth';
import { useDispatch } from 'react-redux';
import { compose } from 'recompose';
import { useSelector } from 'react-redux';
import withPageView from '../../../../helpers/tracking/hocs/withPageView';
import { getBrand } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { trackPage } from '../../../../helpers/tracking/common/commonTrackingHelpers';
import { Container, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { isEmpty } from '../../../../pages/Account/utils/object';
import EventDispatcher from '../../state/reducers/EventDispatcher';
import GraphqlSeoHeadTags from '../../../../components/GraphqlComponents/GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';
import SiteLoader from '../../../../pages/Account/checkout/components/Partials/SiteLoader/SiteLoader';

import RecipientLandingPageController from './RecipientLandingPageController';
import RecipientLandingErrorPage from './RecipientLandingErrorPage';
import RecipientVerifyForm from './RecipientVerifyForm';
import verifyRecipientByTrackingNumber from './apis/verifyRecipientByTrackingNumber';
import verifyRecipientByParams from './apis/verifyRecipientByParams';

const useStyles = makeStyles((theme) => ({
  recipientVerifyContainer: {
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: "column",
      alignItems: "center",
      paddingLeft: '0',
      paddingRight: '0',
    '& button': {
      backgroundColor: `${theme.palette.colorPrimaryAccountCtaButton}!important`,
      color: '#fff !important',
      borderRadius: '3px !important',
      padding: theme.spacing(1, 3),
      marginRight: '0 !important',
      border: 'none !important',
      '&:hover': {
        backgroundColor: '#482a75 !important',
      },
      '&.Mui-disabled': {
         color: 'rgba(0, 0, 0, 0.26)',
         backgroundColor: `${theme.palette.common.grey} !important`,
      },
    },
  },
  recipientInnerContainer: {
    display: 'flex', 
    flexDirection: 'column', 
    alignItems: 'center',
    maxWidth: '430px',
    '& img': {
      height: '100%',
      width: '100%',
    }
  },
  resetPageMargin: {
      width: '100%!important',
      margin: '0!important'
  },
  loaderBackground: {
   backgroundColor: `${theme.palette.common.white}!important`,
  }
}));


const RecipientVerify = () => {
  const { pathname } = useLocation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const cardIsleParams = pathname?.split('/thankyoucards/')[1];
  
  const brand = useSelector(getBrand);
  const getRecipientVerifyDataFromState = useSelector(state => state.account?.recipientLandingPage);
  
  const [data, setData] = useState(getRecipientVerifyDataFromState);
  const [apiError, setApiError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);  

  const processRecipientVerify = useMutation({
   mutationFn: verifyRecipientByTrackingNumber,
   onSuccess: (data) => {
      if (data && data.data !== undefined) {
         setData(data.data);
      }
      setIsLoading(false);
   },
   onError: (error) => {
      setApiError(true);
      setIsLoading(false);
      mbpLogger.logError({
          function: 'processRecipientVerify',
          appName: process.env.npm_package_name,
          module: 'RecipientVerify',
          message: `Error While Verifying Recipient`,
          error,
      });
   }
  });

  const processRecipientVerifyByParams = useMutation({
   mutationFn: verifyRecipientByParams,
   onSuccess: (data) => {
      if (data && data.data !== undefined) {
         setData(data.data);
      }
      setIsLoading(false);
   },
   onError: (error) => {
      setApiError(true);
      setIsLoading(false);
      mbpLogger.logError({
          function: 'processRecipientVerifyByParams',
          appName: process.env.npm_package_name,
          module: 'RecipientVerify',
          message: `Error While Verifying Recipient By Params`,
          error,
      });
   }
  });

  const fetchRecipientData = async (verificationState) => {
   setIsLoading(true);
   const token = await auth.getAccessTokenSafely();
   let recipientVerifyPayload = {};
   if (cardIsleParams) {
      recipientVerifyPayload = {
         cardIsleParams,
         token
      };
      processRecipientVerifyByParams.mutate(recipientVerifyPayload);
   } else {
      recipientVerifyPayload = {
         requestBody: { 
            trackingNumber: verificationState.trackingNumber.replace(/\s/g, ''),
            lastName: verificationState.lastName
         },
         token
      };
      processRecipientVerify.mutate(recipientVerifyPayload);
   };
 };

 useEffect(() => {
   const mobileContainer = document.getElementById("mobile-main-container");

   if (cardIsleParams) {
      fetchRecipientData(cardIsleParams)
   };

   mobileContainer?.classList.add(classes.resetPageMargin);
   return () => {
      mobileContainer?.classList.remove(classes.resetPageMargin)
   };
  }, []);

  useEffect(() => {
      if (!cardIsleParams && !isEmpty(data)) {
         dispatch(EventDispatcher.setRecipientVerifyData(data));
      }
   }, [data])

  const seo = {
      page_title: `View Your Beautiful E-Card & Send A Thank You Today | ${brand?.['display-name']}`,
      meta_description: `You received an e-card from someone who cares about you! View your card and send a thank you right back with ${brand?.['display-name']}!`,
      location: { pathname: '/account/thankyoucards' },
  };

  return (
   <React.Fragment>
    <Helmet>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
      <link href="https://fonts.googleapis.com/css2?family=Roboto&display=swap" rel="stylesheet" />
    </Helmet>
    <GraphqlSeoHeadTags seoData={seo} />
    {cardIsleParams && isEmpty(data) && !apiError ? 
      <SiteLoader classes={classes}/> :
      <Container className={classes.recipientVerifyContainer}>
         <Grid container spacing={0} className={classes.recipientInnerContainer}>
         {apiError && (
            <React.Fragment>
               <RecipientLandingErrorPage cardIsleParams={cardIsleParams} />
            </React.Fragment>
         )}
         {data?.statusCode && (
            <React.Fragment>
               {(data.statusCode === 2013 || data.statusCode === 2001 || data.statusCode === 2011) ? 
               <RecipientLandingPageController recipientVerifyData={data} cardIsleParams={cardIsleParams} brandId={brand['brand-id']} /> 
               : 
               <RecipientLandingErrorPage cardIsleParams={cardIsleParams} />}
            </React.Fragment>
         )}
         {!cardIsleParams && !apiError && !data?.statusCode && (
            <RecipientVerifyForm fetchRecipientData={fetchRecipientData} isLoading={isLoading} />
         )}
         </Grid>
      </Container>
   }
   </React.Fragment>
);
};


const enhance = compose(
   trackPage({
      type: 'recipientverify',
      name: 'recipient verify',
      accountPage: true,
   }),
   withPageView({
      pageViewSuccessTest: () => true,
   }),
);

export default enhance(RecipientVerify);